import { maxUint256 } from 'viem'
import { ChainId } from 'helpers/enums'
import { LiquidityPoolContractType } from 'graph/liquidity'
import type { IconName } from 'components/ui'


const baseUrl = process.env.NEXT_PUBLIC_BASE_URL as string
const minDeposit = process.env.NEXT_PUBLIC_MIN_DEPOSIT as string
const isDev = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'development'
const isPreprod = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'preprod'
const isProd = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production'
const isTknVisible = Boolean(process.env.NEXT_PUBLIC_IS_TKN_VISIBLE)

const links = {
  about: 'https://gem.azuro.org/knowledge-hub/introduction/what-is-azuro',
  audits: 'https://gem.azuro.org/contracts/audits',
  docs: 'https://gem.azuro.org',
  build: 'https://gem.azuro.org/hub',
  buildersHub: 'https://hub.azuro.org/',
  privacy: 'https://gem.azuro.org/privacy-policy',
  terms: 'https://gem.azuro.org/terms-of-use',
  wavesDoc: 'https://gem.azuro.org/knowledge-hub/airdrops/azuro-waves/overview',
  stakingDoc: 'https://gem.azuro.org/knowledge-hub/azur/st-azur',

  // token
  azur: 'https://gem.azuro.org/knowledge-hub/azur/tokenomics',

  github: 'https://github.com/Azuro-protocol',
  twitter: 'https://twitter.com/azuroprotocol',
  discord: 'https://discord.gg/azuro',
  medium: 'https://azuroprotocol.medium.com',
  dune: 'https://dune.com/azuro/stats',

  app: '/app',
  oracleStart:
    'https://docs.google.com/forms/d/1pbDffqG38cVwvb3Qqm9p9aD2YYlE6POI_6Jyg1JzDys/edit',
  frontendStart: 'https://azuro.typeform.com/to/v80viKml',
  liquidity: '/app/liquidity',
  governance: '/app/governance',
  staking: '/app/staking',
  vesting: '/app/vesting',
  dexFarming: '/app/dex-farming',
  airdrop: '/app/airdrop',
  waves: '/app/waves',
} as const

const rpcByChains: Record<ChainId, string> = {
  [ChainId.Ethereum]: 'https://ethereum-rpc.publicnode.com',
  // [ChainId.Goerli]: 'https://ethereum-goerli.publicnode.com',
  [ChainId.Gnosis]: 'https://gnosis-rpc.publicnode.com',
  [ChainId.Polygon]: 'https://polygon-bor-rpc.publicnode.com',
  [ChainId.PolygonAmoy]: 'https://polygon-amoy-bor-rpc.publicnode.com',
  [ChainId.Arbitrum]: 'https://arbitrum-one-rpc.publicnode.com',
  [ChainId.ArbitrumGoerli]: 'https://arbitrum-goerli.publicnode.com',
  [ChainId.Linea]: 'https://rpc.linea.build',
  [ChainId.LineaGoerli]: 'https://rpc.goerli.linea.build',
  [ChainId.Chiliz]: 'https://chiliz-rpc.publicnode.com',
  [ChainId.ChilizSpicy]: 'https://chiliz-spicy-rpc.publicnode.com',
  [ChainId.Base]: 'https://base-rpc.publicnode.com',
  [ChainId.BaseSepolia]: 'https://base-sepolia-rpc.publicnode.com',
} as const

type ChainData = {
  chainName: string;
  icon: string;
  iconName: IconName;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  wrappedNative: {
    name: string;
    symbol: string;
    decimals: number;
    address: Address;
  };
  rpcUrls: string[];
  blockExplorerUrls: string[];
};

const chainsMap: Record<ChainId, ChainData> = {
  [ChainId.Ethereum]: {
    chainName: 'Ethereum',
    icon: '/images/icons/networks/eth.svg',
    iconName: 'networks/eth',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    },
    rpcUrls: [ rpcByChains[ChainId.Ethereum] ],
    blockExplorerUrls: [ 'https://etherscan.io' ],
  },
  [ChainId.Gnosis]: {
    chainName: 'Gnosis',
    icon: '/images/icons/networks/gnosis.svg',
    iconName: 'networks/gnosis',
    nativeCurrency: { name: 'XDAI', symbol: 'XDAI', decimals: 18 },
    wrappedNative: {
      name: 'WXDAI',
      symbol: 'WXDAI',
      decimals: 18,
      address: '0xe91D153E0b41518A2Ce8Dd3D7944Fa863463a97d',
    },
    rpcUrls: [ rpcByChains[ChainId.Gnosis] ],
    blockExplorerUrls: [ 'https://gnosisscan.io' ],
  },
  [ChainId.Polygon]: {
    chainName: 'Polygon',
    icon: '/images/icons/networks/polygon.svg',
    iconName: 'networks/polygon',
    nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
    wrappedNative: {
      name: 'WPOL',
      symbol: 'WPOL',
      decimals: 18,
      address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
    },
    rpcUrls: [ rpcByChains[ChainId.Polygon] ],
    blockExplorerUrls: [ 'https://polygonscan.com' ],
  },
  [ChainId.PolygonAmoy]: {
    chainName: 'Polygon Amoy',
    icon: '/images/icons/networks/polygon.svg',
    iconName: 'networks/polygon',
    nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
    wrappedNative: {
      name: 'WPOL',
      symbol: 'WPOL',
      decimals: 18,
      address: '0x0',
    }, // TODO unknown address
    rpcUrls: [ rpcByChains[ChainId.PolygonAmoy] ],
    blockExplorerUrls: [ 'https://amoy.polygonscan.com' ],
  },
  [ChainId.Arbitrum]: {
    chainName: 'Arbitrum One',
    icon: '/images/icons/networks/arbitrum.svg',
    iconName: 'networks/arbitrum',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1',
    },
    rpcUrls: [ rpcByChains[ChainId.Arbitrum] ],
    blockExplorerUrls: [ 'https://arbiscan.io' ],
  },
  [ChainId.ArbitrumGoerli]: {
    chainName: 'Arbitrum Goerli',
    icon: '/images/icons/networks/arbitrum.svg',
    iconName: 'networks/arbitrum',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0x0',
    }, // goerli is shut down
    rpcUrls: [ rpcByChains[ChainId.ArbitrumGoerli] ],
    blockExplorerUrls: [ 'https://goerli.arbiscan.io' ],
  },
  [ChainId.Linea]: {
    chainName: 'Linea',
    icon: '/images/icons/networks/linea.svg',
    iconName: 'networks/linea',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f',
    },
    rpcUrls: [ rpcByChains[ChainId.Linea] ],
    blockExplorerUrls: [ 'https://lineascan.build' ],
  },
  [ChainId.LineaGoerli]: {
    chainName: 'Linea Goerli',
    icon: '/images/icons/networks/linea.svg',
    iconName: 'networks/linea',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0x0',
    }, // TODO unknown address
    rpcUrls: [ rpcByChains[ChainId.LineaGoerli] ],
    blockExplorerUrls: [ 'https://goerli.lineascan.build' ],
  },
  [ChainId.Chiliz]: {
    chainName: 'Chiliz',
    icon: '/images/icons/networks/chiliz.svg',
    iconName: 'networks/chiliz',
    nativeCurrency: { name: 'CHZ', symbol: 'CHZ', decimals: 18 },
    wrappedNative: {
      name: 'WCHZ',
      symbol: 'WCHZ',
      decimals: 18,
      address: '0x677F7e16C7Dd57be1D4C8aD1244883214953DC47',
    },
    rpcUrls: [ rpcByChains[ChainId.Chiliz] ],
    blockExplorerUrls: [ 'https://chiliscan.com' ],
  },
  [ChainId.ChilizSpicy]: {
    chainName: 'Chiliz Spicy',
    icon: '/images/icons/networks/chiliz.svg',
    iconName: 'networks/chiliz',
    nativeCurrency: { name: 'CHZ', symbol: 'CHZ', decimals: 18 },
    wrappedNative: {
      name: 'WCHZ',
      symbol: 'WCHZ',
      decimals: 18,
      address: '0x721ef6871f1c4efe730dce047d40d1743b886946',
    },
    // TODO weird, 0x721ef6871f1c4efe730dce047d40d1743b886946 address taken from subgraph,
    //       while 0x678c34581db0a7808d0aC669d7025f1408C9a3C6 has a checkmark in explorer - added on 19.04.2024 by sonatskiy
    // wrappedNative: { name: 'WCHZ', symbol: 'WCHZ', decimals: 18, address: '0x678c34581db0a7808d0aC669d7025f1408C9a3C6' },
    rpcUrls: [ rpcByChains[ChainId.Chiliz] ],
    blockExplorerUrls: [ 'https://testnet.chiliscan.com' ],
  },
  [ChainId.Base]: {
    chainName: 'Base',
    icon: '/images/icons/networks/base.svg',
    iconName: 'networks/base',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0x4200000000000000000000000000000000000006',
    },
    rpcUrls: [ rpcByChains[ChainId.Base] ],
    blockExplorerUrls: [ 'https://basescan.org' ],
  },
  [ChainId.BaseSepolia]: {
    chainName: 'Base Sepolia',
    icon: '/images/icons/networks/base.svg',
    iconName: 'networks/base',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    // custom weth
    wrappedNative: {
      name: 'WETH',
      symbol: 'WETH',
      decimals: 18,
      address: '0x9e09f213ff75e53d52e9e777a6567a68683e935f',
    },
    rpcUrls: [ rpcByChains[ChainId.BaseSepolia] ],
    blockExplorerUrls: [ 'https://sepolia.basescan.org' ],
  },
}

const azurTokenChainIdPrimary = parseInt(
  process.env.NEXT_PUBLIC_AZUR_TOKEN_CHAIN_ID as string
) as ChainId.Ethereum | ChainId.Polygon
const azurTokenChainIds = [ azurTokenChainIdPrimary, ChainId.Base ] as const

export type AzurTokenChainId = (typeof azurTokenChainIds)[number];

const azurPointsWaveId = parseInt(
  process.env.NEXT_PUBLIC_AZUR_POINTS_WAVE_ID as string
)

const azurToken = {
  name: 'Azuro',
  // image: `${baseUrl}/images/icons/tokens/azuro.svg`,
  // metamask accepts base64, not url
  image:
    'data:image/svg+xml;base64,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',
  // duct-tape for adding token in dev, it must be equal to symbol returned by the contract
  symbol: isProd ? 'AZUR' : process.env.NEXT_PUBLIC_AZUR_TOKEN_SYMBOL || 'AZUR',
  decimals: 18,
  chainId: azurTokenChainIdPrimary,
  address: process.env.NEXT_PUBLIC_AZUR_TOKEN as Address,
  vestingContractAddress: process.env.NEXT_PUBLIC_AZUR_VESTING as Address,
  airdropContractAddress: process.env.NEXT_PUBLIC_AZUR_AIRDROP as Address,
  stakingAddress: process.env.NEXT_PUBLIC_AZUR_STAKING as Address,
} as const

const azurStakedToken = {
  name: 'Staked $AZUR',
  // metamask accepts base64, not url
  image:
    'data:image/svg+xml;base64,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',
  symbol: isProd
    ? 'stAZUR'
    : process.env.NEXT_PUBLIC_STAZUR_TOKEN_SYMBOL || 'stAZUR',
  decimals: 18,
  exchangeRateDecimals: 18,
  chainId: azurTokenChainIdPrimary,
  address: process.env.NEXT_PUBLIC_STAZUR_TOKEN as Address,
  apolloClientKey: 'staking',
} as const

const azurTokenBase = {
  ...azurToken,
  address: process.env.NEXT_PUBLIC_AZUR_TOKEN_BASE as Address,
  chainId: ChainId.Base,
  symbol: 'AZUR',
  vestingContractAddress: undefined,
  airdropContractAddress: undefined,
  stakingAddress: undefined,
} as const

const azurStakedTokenBase = {
  ...azurStakedToken,
  address: process.env.NEXT_PUBLIC_STAZUR_TOKEN_BASE as Address,
  chainId: ChainId.Base,
  symbol: 'stAZUR',
  apolloClientKey: 'stakingBase',
} as const

const azurByChainId = {
  [azurTokenChainIdPrimary]: {
    token: azurToken,
    stakedToken: azurStakedToken,
  },
  [ChainId.Base]: {
    token: azurTokenBase,
    stakedToken: azurStakedTokenBase,
  },
} as const

const dexFarming = {
  chainId: azurTokenChainIdPrimary,
  // same address for polygon, arbitrum and mainnet
  // https://github.com/Uniswap/v3-staker/releases/tag/v1.0.2
  stakerAddress: '0xe34139463bA50bD61336E0c446Bd8C0867c6fE65' as Address,
  // same address for polygon, arbitrum and mainnet
  // https://docs.uniswap.org/contracts/v3/reference/deployments
  positionManagerAddress:
    '0xC36442b4a4522E871399CD717aBDD847Ab11FE88' as Address,
  tokenAddress: azurToken.address,
} as const

const affiliateRewardAddressByChainId = {
  [ChainId.Gnosis]: process.env.NEXT_PUBLIC_AFFILIATE_REWARD_GNOSIS as Address,
  [ChainId.Polygon]: process.env
    .NEXT_PUBLIC_AFFILIATE_REWARD_POLYGON as Address,
  [ChainId.PolygonAmoy]: process.env
    .NEXT_PUBLIC_AFFILIATE_REWARD_POLYGON as Address,
  [ChainId.Arbitrum]: process.env
    .NEXT_PUBLIC_AFFILIATE_REWARD_ARBITRUM as Address,
  [ChainId.ArbitrumGoerli]: process.env
    .NEXT_PUBLIC_AFFILIATE_REWARD_ARBITRUM as Address,
  [ChainId.Linea]: process.env.NEXT_PUBLIC_AFFILIATE_REWARD_LINEA as Address,
  [ChainId.LineaGoerli]: process.env
    .NEXT_PUBLIC_AFFILIATE_REWARD_LINEA as Address,
}

const iconNameByTokenSymbol: Record<string, IconName> = {
  ETH: 'tokens/eth',
  WETH: 'tokens/weth',
  USDC: 'tokens/usdc',
  POL: 'tokens/pol',
  BNB: 'tokens/bnb',
  XDAI: 'tokens/xdai',
  WXDAI: 'tokens/wxdai',
  USDT: 'tokens/usdt',
  MNY: 'tokens/usdt', // test token instead of USDT in polygon
  CHZ: 'tokens/chz', // chiliz
  WCHZ: 'tokens/wchz', // chiliz
  [azurToken.symbol]: 'tokens/azuro',
  [azurStakedToken.symbol]: 'tokens/azuro-staked',
  [azurStakedToken.symbol.toUpperCase()]: 'tokens/azuro-staked',
} as const

export default {
  isDev,
  activePoolType: LiquidityPoolContractType.V3,
  isPreprod,
  isProd,
  isTknVisible,
  baseUrl,
  links,
  chainsMap,
  rpcByChains,
  maxUint256,
  azurToken,
  azurStakedToken,
  azurTokenBase,
  azurStakedTokenBase,
  azurByChainId,
  azurTokenChainIds,
  azurPointsWaveId,
  dexFarming,
  iconNameByTokenSymbol,
  affiliateRewardAddressByChainId,
  stakingInterestRateDecimals: 12,
  liquidityManagerDepositRateDecimals: 12,
  coreV1Address: process.env.NEXT_PUBLIC_CORE_V1_ADDRESS as Address,
  minDeposit,
}
